import React from "react";
import { format, parse } from "date-fns";
import { CalendarDays, Clock, ChevronDown } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";

// ---- Venue reservation time slots (12-hour AM/PM), 5:00 PM – 1:30 AM ----
export const TIME_SLOTS = (() => {
  const slots = [];
  // 17:00 -> 01:30 next day, 30-min steps
  for (let m = 17 * 60; m <= 25 * 60 + 30; m += 30) {
    const total = m % (24 * 60);
    let h = Math.floor(total / 60);
    const min = total % 60;
    const ampm = h >= 12 && h < 24 ? "PM" : "AM";
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    slots.push(`${h12}:${min.toString().padStart(2, "0")} ${ampm}`);
  }
  return slots;
})();

const darkCalendarClassNames = {
  caption_label: "text-nala-cream font-medium",
  head_cell: "text-nala-cream-muted rounded-md w-9 font-normal text-[0.75rem]",
  day: "h-9 w-9 p-0 font-normal text-nala-cream hover:bg-nala-gold/20 rounded-md aria-selected:opacity-100",
  day_selected: "bg-nala-gold text-nala-black hover:bg-nala-gold focus:bg-nala-gold",
  day_today: "text-nala-gold font-semibold",
  day_disabled: "text-nala-cream-muted/30",
  day_outside: "text-nala-cream-muted/40",
  nav_button: "h-7 w-7 bg-transparent p-0 text-nala-gold hover:opacity-100 opacity-70 border border-nala-gold/30 rounded-md",
};

export function DatePickerField({ value, onChange, placeholder = "Select date", triggerClassName = "" }) {
  const [open, setOpen] = React.useState(false);
  const selected = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;

  const base =
    "w-full flex items-center justify-between gap-2 px-4 py-3 text-left border transition-colors focus:outline-none";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${base} ${triggerClassName}`}>
          <span className={value ? "" : "opacity-60"}>
            {value ? format(selected, "EEE, MMM d, yyyy") : placeholder}
          </span>
          <CalendarDays size={17} className="shrink-0 text-nala-gold" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 bg-nala-charcoal border border-nala-gold/30 shadow-2xl">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => {
            if (d) onChange(format(d, "yyyy-MM-dd"));
            setOpen(false);
          }}
          disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
          classNames={darkCalendarClassNames}
        />
      </PopoverContent>
    </Popover>
  );
}

export function TimePickerField({ value, onChange, placeholder = "Select time", triggerClassName = "" }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger
        className={`w-full px-4 py-3 h-auto border rounded-none focus:ring-0 focus:ring-offset-0 [&>svg]:hidden ${triggerClassName}`}
      >
        <div className="flex items-center justify-between w-full gap-2">
          <SelectValue placeholder={<span className="opacity-60">{placeholder}</span>} />
          <Clock size={17} className="shrink-0 text-nala-gold" />
        </div>
      </SelectTrigger>
      <SelectContent className="bg-nala-charcoal border border-nala-gold/30 text-nala-cream max-h-64">
        {TIME_SLOTS.map((t) => (
          <SelectItem
            key={t}
            value={t}
            className="text-nala-cream focus:bg-nala-gold focus:text-nala-black cursor-pointer"
          >
            {t}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
